import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contractsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    customer_name: '', customer_code: '', contact_person: '', phone: '', email: '',
    gst_number: '', pan_number: '', address_1: '', address_2: '', city: '',
    state_province: '', country: 'India', pin_code: '', website: '', remarks: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (isEdit) loadCustomer(); }, [id]);

  const loadCustomer = async () => {
    try {
      const res = await contractsAPI.getCustomer(id);
      const d = res.data;
      setForm({
        customer_name: d.customer_name || '', customer_code: d.customer_code || '',
        contact_person: d.contact_person || '', phone: d.phone || '', email: d.email || '',
        gst_number: d.gst_number || '', pan_number: d.pan_number || '',
        address_1: d.address_1 || '', address_2: d.address_2 || '', city: d.city || '',
        state_province: d.state_province || '', country: d.country || 'India',
        pin_code: d.pin_code || '', website: d.website || '', remarks: d.remarks || '',
      });
    } catch { toast.error('Failed to load customer'); navigate('/contracts/customers'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_code) { toast.error('Name and code are required'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await contractsAPI.updateCustomer(id, form);
        toast.success('Customer updated');
        navigate(`/contracts/customers/${id}`);
      } else {
        const res = await contractsAPI.createCustomer(form);
        toast.success('Customer created');
        navigate(`/contracts/customers/${res.data.id}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4 max-w-4xl" data-testid="customer-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="font-heading text-xl font-bold tracking-tight">{isEdit ? 'Edit Customer' : 'New Customer'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Basic Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Customer Code *</Label>
              <Input value={form.customer_code} onChange={e => u('customer_code', e.target.value)}
                disabled={isEdit} placeholder="e.g. CUST-001" className="h-8 text-xs font-mono" data-testid="customer-code-input" />
            </div>
            <div>
              <Label className="text-xs">Customer Name *</Label>
              <Input value={form.customer_name} onChange={e => u('customer_name', e.target.value)}
                placeholder="Company name" className="h-8 text-xs" data-testid="customer-name-input" />
            </div>
            <div>
              <Label className="text-xs">Contact Person</Label>
              <Input value={form.contact_person} onChange={e => u('contact_person', e.target.value)}
                className="h-8 text-xs" data-testid="customer-contact" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={e => u('phone', e.target.value)}
                className="h-8 text-xs" data-testid="customer-phone" />
            </div>
            <div>
              <Label className="text-xs">Email <span className="text-muted-foreground font-normal">(comma-separate for multiple)</span></Label>
              <Input type="text" value={form.email} onChange={e => u('email', e.target.value)}
                placeholder="e.g. accounts@acme.com, dispatch@acme.com"
                className="h-8 text-xs" data-testid="customer-email" />
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input value={form.website} onChange={e => u('website', e.target.value)}
                className="h-8 text-xs" data-testid="customer-website" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Tax & Compliance</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">GST Number</Label>
              <Input value={form.gst_number} onChange={e => u('gst_number', e.target.value)}
                className="h-8 text-xs font-mono" data-testid="customer-gst" />
            </div>
            <div>
              <Label className="text-xs">PAN Number</Label>
              <Input value={form.pan_number} onChange={e => u('pan_number', e.target.value)}
                className="h-8 text-xs font-mono" data-testid="customer-pan" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Address</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Address Line 1</Label>
              <Input value={form.address_1} onChange={e => u('address_1', e.target.value)}
                className="h-8 text-xs" data-testid="customer-address1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Address Line 2</Label>
              <Input value={form.address_2} onChange={e => u('address_2', e.target.value)}
                className="h-8 text-xs" data-testid="customer-address2" />
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input value={form.city} onChange={e => u('city', e.target.value)}
                className="h-8 text-xs" data-testid="customer-city" />
            </div>
            <div>
              <Label className="text-xs">State/Province</Label>
              <Input value={form.state_province} onChange={e => u('state_province', e.target.value)}
                className="h-8 text-xs" data-testid="customer-state" />
            </div>
            <div>
              <Label className="text-xs">Country</Label>
              <Input value={form.country} onChange={e => u('country', e.target.value)}
                className="h-8 text-xs" data-testid="customer-country" />
            </div>
            <div>
              <Label className="text-xs">PIN Code</Label>
              <Input value={form.pin_code} onChange={e => u('pin_code', e.target.value)}
                className="h-8 text-xs" data-testid="customer-pin" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <Label className="text-xs">Remarks</Label>
            <Textarea value={form.remarks} onChange={e => u('remarks', e.target.value)}
              className="text-xs min-h-[60px]" data-testid="customer-remarks" />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-customer-btn">
            <Save className="w-4 h-4 mr-1" /> {loading ? 'Saving...' : isEdit ? 'Update' : 'Create Customer'}
          </Button>
        </div>
      </form>
    </div>
  );
}
